// Committed generic mock seeds. Used by the dev-mock interceptor when no
// local override exists. Intentionally bland ("Acme v. Beacon (Demo)") so
// CI can build without pulling in the scenario-specific dataset.
//
// Devs who want the full scenario data run `npm run setup-mock`, which
// writes `_mockData.local.ts` (gitignored). A vite plugin in
// `vite.config.ts` redirects this module's specifier to the `.local` file
// whenever it's present, so the real seeds take over transparently.

import type {
  CaseIssue,
  DocumentMeta,
  DocumentReview,
  IndividualRequest,
  RequestMatch,
  RequestObjection,
  RequestSet,
  Reviewer,
} from '../incoming_requests/types';

export const MOCK_ISSUES: CaseIssue[] = [
  { id: 'iss-breach', label: 'Breach of Contract', color: '#7C3AED', sortOrder: 0,
    description: 'Alleged breach of the licensing agreement. Plaintiff contends that Beacon sublicensed the brand to third-party retailers in Nevada and Arizona without written consent and stopped remitting quarterly royalty statements after Q2.' },
  { id: 'iss-royalties', label: 'Unpaid Royalties', color: '#10B981', sortOrder: 1,
    description: "Plaintiff Crumb & Co. claims that VGM Management (a Vapora subsidiary) stocked the Crumb Oak Street store and Crumb Express store with Vapora's own product brands (sold through the Vapora entities Foundry Distribution and RLT Holdings LLC) instead of competing products, such as those sold by Crumb & Co. Plaintiffs claim that Vapora thereby diverted to itself sales and profits that would otherwise have gone to Crumb & Co. Plaintiffs allege that 80% or more of the Crumb Oak Street and Crumb Express stores' inventory consisted of Vapora products. Plaintiffs claim that this improper stocking breached the operating agreements governing the Crumb Oak Street and Crumb Express stores. Specifically, Plaintiffs allege that Crumb & Co., through its designated manager, had the authority to select inventory. Plaintiffs claim Vapora either disregarded the Crumb & Co. manager's inventory selections or cancelled orders for those products." },
  { id: 'iss-damages', label: 'Damages', color: '#EF4444', sortOrder: 2,
    description: "Delgado alleges that Wren's agreed-to responsibilities for Kestrel Partners included memorializing their oral Kestrel Partners agreement, and documenting Kestrel Partners' investments and the Partnership's interests therein. Delgado alleges he \"periodically\" asked Wren for copies of Partnership documents. The earliest time he claims to have done so was in April 2017, when Lumen, one of the \"Portfolio Companies,\" was allegedly being sold for $7 billion. He claims he emailed in July 2017 to ask for the documents again, and then emailed again in September 2017 to ask for documents regarding Lumen and Vector Shield (another Portfolio Company, allegedly being sold for $3 billion). Delgado alleges Wren repeatedly agreed to provide the requested documents but never did so. Wren denies this, and says it was only after Delgado found out about the upcoming sale of Lumen Labs Corporation to Tradewell (by secretly accessing and reviewing the contents of Wren's marcus@kestrelvc.com Google account without Wren's authorization or consent) that he approached Wren in April 2017 claiming falsely he had a 35% equity interest in Kestrel Partners." },
  { id: 'iss-privilege', label: 'Privilege Review', color: '#3B82F6', sortOrder: 3,
    description: 'Documents flagged for privilege review.' },
  { id: 'iss-negotiation', label: 'Pre-Negotiation Intent', color: '#F59E0B', sortOrder: 4,
    description: 'Communications reflecting the parties\u2019 intent during agreement negotiations.' },
  { id: 'iss-amendments', label: 'Amendments & Addenda', color: '#8B5CF6', sortOrder: 5,
    description: 'Side letters, amendments, and addenda to the principal agreement.' },
  { id: 'iss-default', label: 'Notice of Default', color: '#DC2626', sortOrder: 6,
    description: 'Notices of default, cure letters, and related correspondence.' },
  { id: 'iss-financials', label: 'Financial Records', color: '#0EA5E9', sortOrder: 7,
    description: 'Ledgers, invoices, and bookkeeping records relevant to the royalty claims.' },
  { id: 'iss-third-party', label: 'Third-Party Communications', color: '#14B8A6', sortOrder: 8,
    description: 'Communications with advisors, auditors, or other non-party witnesses.' },
  { id: 'iss-trade-secrets', label: 'Trade Secrets', color: '#F97316', sortOrder: 9,
    description: 'Proprietary formulations, supplier lists, and other trade-secret material.' },
  { id: 'iss-regulatory', label: 'Regulatory Compliance', color: '#EAB308', sortOrder: 10,
    description: 'Filings and correspondence with licensing regulators.' },
  { id: 'iss-counterclaim', label: 'Counterclaim Support', color: '#EC4899', sortOrder: 11,
    description: 'Evidence supporting Responding Party\u2019s counterclaims.' },
  { id: 'iss-inventory-orders', label: "Breach of operating agreements: VGM overrode or cancelled the Crumb & Co. manager's inventory orders", color: '#A78BFA', sortOrder: 12,
    description: "Crumb & Co. alleges that Section 6.3 of each store's operating agreement vested inventory selection in the \"Designated Manager\" appointed by Crumb & Co., and that VGM Management's role was limited to back-office functions: payroll, point-of-sale administration, and vendor payment. Crumb & Co. identifies at least 214 purchase orders submitted by its designated manager, Tasha Rinaldi, between June 2022 and February 2024 that were either cancelled by VGM before transmission to the vendor or replaced with orders for equivalent Vapora-brand products. Crumb & Co. relies principally on a series of emails in which VGM's regional operations lead, Owen Pratt, instructed store staff to \"hold anything non-Foundry until I sign off,\" and on VGM's internal ordering-system audit trail, which shows Pratt's credentials were used to void Rinaldi's orders. Crumb & Co. contends each cancellation was a separate breach of Section 6.3 and of the implied covenant of good faith and fair dealing.\n\nVapora responds that Section 6.3 is subject to Section 4.2, which requires all vendor relationships to comply with the stores' licensing and compliance program, and that the cancelled orders were for products from vendors that had not cleared compliance review. Vapora further argues that Crumb & Co. ratified VGM's practice by accepting quarterly distributions without objection through 2023, and that any damages are limited to the margin difference between the ordered and substituted products rather than the lost sales Crumb & Co. claims." },
];

// Productions — received (opposing counsel) vs outgoing (client's own docs
// awaiting review before production). Counts are illustrative and don't have
// to line up with MOCK_DOCUMENTS — the dashboard aggregates these directly.
export interface MockProduction {
  id: string;
  productionRef: string;
  productionName: string;
  direction: 'received' | 'outgoing';
  sortOrder: number;
  docCount: number;
  reviewedCount: number;
  // Custodial split — outgoing collections carry a custodian; `kind` defaults
  // from direction (outgoing → collection) in toWireProduction when omitted.
  // When >1 custodian, the production fans out into per-custodian virtual
  // collections (the #630 multi-custodian shape).
  custodians?: string[];
  // #545 — 'collection' (our-side, client-collected) vs 'formal_production'
  // (opposing counsel's served set). Defaults are derived from `direction`.
  kind?: 'collection' | 'formal_production';
  // #621 — optional production-level Bates span; a formal_production (received)
  // tile with a begin value renders the productions-only Bates line on the card.
  batesBegin?: string | null;
  batesEnd?: string | null;
  // #545 — links a received production to the party that produced it. Resolved
  // to a `producing_party` object by `toWireProduction` (via MOCK_PARTIES), so
  // the dashboard can section tiles under Plaintiffs'/Defendants'/Unclassified.
  producingPartyId?: string | null;
}

// Case parties — a producing party (received productions, #545) AND/OR the
// responding party a Request Set is served on behalf of (#630). Its canonical
// name + aliases are the custodian keys the server's RFP match scope filters on
// (api/lib/rfp_match_scope.py `fetch_responding_custodian_names`).
export interface MockParty {
  id: string;
  canonicalName: string;
  shortName: string | null;
  roles: string[];
  aliases: string[];
  notes: string | null;
  isActive: boolean;
}

// Case parties for the synthetic Acme/Beacon demo matter. Present in the
// committed seed so the Productions grid renders its party sections without
// the .local scenario. Structurally matches the `MockPartySeed` shape
// loadMockSeeds expects. Acme/Beacon are producing parties (#545 production
// sections); Whitaker/Delacroix are the responding parties the demo Request
// Sets are served on (#630) — their names match the our-side collections'
// custodians so the server-authoritative match-scope-count resolves nonzero.
// `party-ghost` is intentionally ABSENT so the "Orphaned" set below exercises
// the dangling-FK fail-closed path (#630/D8: modal shows "—", run disabled).
export const MOCK_PARTIES: MockParty[] = [
  {
    id: 'mock-party-acme',
    canonicalName: 'Acme Corp',
    shortName: 'Acme',
    roles: ['plaintiff'],
    aliases: ['Acme Corporation'],
    notes: null,
    isActive: true,
  },
  {
    id: 'mock-party-beacon',
    canonicalName: 'Beacon Manufacturing, Inc.',
    shortName: 'Beacon',
    roles: ['defendant'],
    aliases: ['Beacon Mfg.'],
    notes: null,
    isActive: true,
  },
  // #1105 — the v2 Claims Matrix fixture's CLIENT. It invents its own party
  // objects (`buildClaimsMatrixV2Seeds`) and this one had no row here at all,
  // which the read-only screen never noticed because its party names arrive on
  // the matrix payload. The setup panel's roster comes from `case_parties`
  // instead — so without this row the one party you need to tick as our client
  // is the one party missing from the list.
  {
    id: 'mock-party-zenith',
    canonicalName: 'Zenith Industries, Inc.',
    shortName: 'Zenith',
    roles: ['defendant'],
    aliases: ['Zenith Logistics'],
    notes: null,
    isActive: true,
  },
  {
    id: 'party-whitaker',
    canonicalName: 'Marcus Whitaker',
    shortName: 'Whitaker',
    roles: ['defendant'],
    aliases: ['M. Whitaker', 'Whitaker, Marcus'],
    notes: null,
    isActive: true,
  },
  {
    id: 'party-delacroix',
    canonicalName: 'Evelyn Delacroix',
    shortName: 'Delacroix',
    roles: ['defendant'],
    aliases: ['E. Delacroix'],
    notes: null,
    isActive: true,
  },
  // Unclassified demo (#1111): only `other`, and more than two aliases, so the
  // Parties screen's badge and "+N more" toggle are visible by hand.
  {
    id: 'party-northwind',
    canonicalName: 'Northwind Holdings LLC',
    shortName: null,
    roles: ['other'],
    aliases: ['Northwind', 'NW Holdings', 'Northwind Holdings, L.L.C.'],
    notes: null,
    isActive: true,
  },
];

export const MOCK_PRODUCTIONS: MockProduction[] = [
  {
    id: 'prod-acme-001',
    productionRef: 'acme_production_001',
    productionName: 'Acme Production 001',
    direction: 'received',
    sortOrder: 0,
    docCount: 4,
    reviewedCount: 0,
    // #621 — synthetic Bates span so the card's productions-only Bates line renders.
    batesBegin: 'ACME-000001',
    batesEnd: 'ACME-000450',
    // Plaintiff's production → sections under "Plaintiffs' Productions".
    producingPartyId: 'mock-party-acme',
  },
  {
    id: 'prod-acme-002',
    productionRef: 'acme_production_002',
    productionName: 'Acme Production 002',
    direction: 'received',
    sortOrder: 1,
    docCount: 1,
    reviewedCount: 0,
    // Left unclassified on purpose → "Unclassified Productions" section.
  },
  {
    id: 'prod-beacon-001',
    productionRef: 'beacon_production_001',
    productionName: 'Beacon Production 001',
    direction: 'received',
    sortOrder: 2,
    docCount: 1,
    reviewedCount: 0,
    // Defendant's production → sections under "Defendants' Productions".
    producingPartyId: 'mock-party-beacon',
  },
  // ── Outgoing (our-side) collections ─────────────────────────────────
  // Surfaced by the Document Discovery "Outgoing" toggle, scoped to docs
  // marked produced. Counts on the outgoing cards are computed live from
  // MOCK_DOCUMENTS' productionStatus, so docCount here is only the fallback.
  {
    id: 'prod-out-whitaker',
    productionRef: 'whitaker_collection',
    productionName: 'Whitaker Custodial Collection',
    direction: 'outgoing',
    sortOrder: 3,
    docCount: 3,
    reviewedCount: 0,
    custodians: ['Marcus Whitaker'],
    kind: 'collection',
  },
  {
    id: 'prod-out-delacroix',
    productionRef: 'delacroix_collection',
    productionName: 'Delacroix Custodial Collection',
    direction: 'outgoing',
    sortOrder: 4,
    docCount: 2,
    reviewedCount: 0,
    custodians: ['Evelyn Delacroix'],
    kind: 'collection',
  },
  // Nothing produced yet — must NOT appear in the outgoing "produced" grid
  // (drop-empty guard), proving the produced scope actually filters.
  {
    id: 'prod-out-unproduced',
    productionRef: 'cfo_collection',
    productionName: 'CFO Working Set (unproduced)',
    direction: 'outgoing',
    sortOrder: 5,
    docCount: 1,
    reviewedCount: 0,
    custodians: ['Dana Cho'],
    kind: 'collection',
  },
  // ── Our-side collections (direction: 'outgoing') — the documents an RFP
  // match run actually scans. `custodians` overlap MOCK_PARTIES canonical
  // names so the server-authoritative match-scope-count resolves a nonzero
  // count for a party-linked set.
  {
    // Multi-custodian collection → fans into two per-custodian virtual
    // collections (the #630 zero-scope shape). Each custodian is credited
    // 6 docs, so Whitaker- and Delacroix-scoped runs each see 6.
    id: 'prod-client-collection-001',
    productionRef: 'client_collection_001',
    productionName: 'Client Collection — Custodial Mailboxes',
    direction: 'outgoing',
    kind: 'collection',
    sortOrder: 6,
    docCount: 6,
    reviewedCount: 0,
    custodians: ['Marcus Whitaker', 'Evelyn Delacroix'],
  },
  {
    // Single-custodian collection → renders as one flat collection card.
    id: 'prod-client-collection-002',
    productionRef: 'client_collection_002',
    productionName: 'Whitaker Personal Files',
    direction: 'outgoing',
    kind: 'collection',
    sortOrder: 7,
    docCount: 3,
    reviewedCount: 0,
    custodians: ['Marcus Whitaker'],
  },
];

export const MOCK_REVIEWERS: Reviewer[] = [
  { email: 'demo@geniac.io', name: 'Demo Partner', role: 'partner' },
  { email: 'associate@geniac.io', name: 'Demo Associate', role: 'associate' },
  { email: 'paralegal@geniac.io', name: 'Demo Paralegal', role: 'paralegal' },
];

const WHITAKER_SET_ID = 'set-demo-whitaker-1';
const DELACROIX_SET_ID = 'set-demo-delacroix-1';
const ORPHAN_SET_ID = 'set-demo-orphan-1';

export const MOCK_REQUEST_SETS: RequestSet[] = [
  {
    id: WHITAKER_SET_ID,
    filename: 'Whitaker-RFPD-Set-1.pdf',
    addressedTo: 'Marcus Whitaker',
    servedAt: '2026-03-25',
    responseDeadline: '2026-04-24',
    productionDeadline: '2026-05-24',
    status: 'ready',
    itemCount: 8,
    // Responding party → the server scope filters to this party's custodial
    // docs. Overlaps 'Marcus Whitaker' across the outgoing custodial collections
    // → the mock match-scope-count resolves a nonzero count for this set.
    respondingPartyId: 'party-whitaker',
  },
  {
    id: DELACROIX_SET_ID,
    filename: 'Delacroix-RFPD-Set-1.pdf',
    addressedTo: 'Evelyn Delacroix',
    servedAt: '2026-03-27',
    responseDeadline: '2026-04-26',
    productionDeadline: '2026-05-26',
    status: 'ready',
    itemCount: 4,
    // Overlaps 'Evelyn Delacroix' on the multi-custodian collection → nonzero scope.
    respondingPartyId: 'party-delacroix',
  },
  {
    // Dangling responding-party FK: the id resolves to no MOCK_PARTIES row, so
    // the server fails closed (party_missing) and the Run-AI modal shows "—"
    // with the run disabled (#630/D8), rather than a misleading "0".
    id: ORPHAN_SET_ID,
    filename: 'Orphaned-RFPD-Set.pdf',
    addressedTo: 'Unknown Custodian',
    servedAt: '2026-03-29',
    responseDeadline: '2026-04-28',
    productionDeadline: '2026-05-28',
    status: 'ready',
    itemCount: 2,
    respondingPartyId: 'party-ghost',
  },
];

const WHITAKER_REQUESTS_RAW: Array<{ no: number; text: string }> = [
  { no: 1, text: 'Any and all of YOUR COMMUNICATIONS with PROPOUNDING PARTY.' },
  { no: 2, text: 'Any and all DOCUMENTS concerning the LICENSING AGREEMENT.' },
  { no: 3, text: 'Any and all DOCUMENTS concerning any royalty payments received by YOU.' },
  { no: 4, text: 'Any and all DOCUMENTS concerning the alleged breach of the LICENSING AGREEMENT.' },
  { no: 5, text: 'Any and all DOCUMENTS concerning DAMAGES YOU claim YOU suffered.' },
  { no: 6, text: 'Any and all COMMUNICATIONS with Evelyn Delacroix concerning the LICENSING AGREEMENT.' },
  { no: 7, text: 'Any and all DOCUMENTS concerning the negotiation of the LICENSING AGREEMENT.' },
  { no: 8, text: 'Any and all DOCUMENTS concerning the May 2023 notice of default.' },
];

const DELACROIX_REQUESTS_RAW: Array<{ no: number; text: string }> = [
  { no: 1, text: 'Any and all of YOUR COMMUNICATIONS with PROPOUNDING PARTY.' },
  { no: 2, text: 'Any and all DOCUMENTS concerning the LICENSING AGREEMENT.' },
  { no: 3, text: 'Any and all COMMUNICATIONS with Marcus Whitaker concerning the LICENSING AGREEMENT.' },
  { no: 4, text: 'Any and all DOCUMENTS concerning DAMAGES YOU claim YOU suffered.' },
];

const ORPHAN_REQUESTS_RAW: Array<{ no: number; text: string }> = [
  { no: 1, text: 'Any and all DOCUMENTS concerning the LICENSING AGREEMENT.' },
  { no: 2, text: 'Any and all COMMUNICATIONS concerning royalty payments.' },
];

// Per-item AI status spread on the Whitaker set (#642) — so every RunStatusBadge
// variant (scanned / stale / failed / running) plus the "stale" filter/count
// render statically without needing a live run. Delacroix + Orphan stay all
// 'not_run' so they demo a clean first run.
const WHITAKER_ITEM_STATUS: Record<number, IndividualRequest['latestRunStatus']> = {
  1: 'scanned',
  2: 'scanned',
  3: 'stale',
  4: 'failed',
  5: 'scanned',
  8: 'running',
};

export const MOCK_REQUESTS: IndividualRequest[] = [
  ...WHITAKER_REQUESTS_RAW.map(r => ({
    id: `${WHITAKER_SET_ID}-r${r.no}`,
    requestSetId: WHITAKER_SET_ID,
    requestNo: r.no,
    requestText: r.text,
    sourcePageStart: 2 + r.no,
    sourcePageEnd: 2 + r.no,
    isVoided: false,
    voidedAt: null,
    voidedByEmail: null,
    voidReason: null,
    latestRunStatus: WHITAKER_ITEM_STATUS[r.no] ?? ('not_run' as const),
  })),
  ...DELACROIX_REQUESTS_RAW.map(r => ({
    id: `${DELACROIX_SET_ID}-r${r.no}`,
    requestSetId: DELACROIX_SET_ID,
    requestNo: r.no,
    requestText: r.text,
    sourcePageStart: 2 + r.no,
    sourcePageEnd: 2 + r.no,
    isVoided: false,
    voidedAt: null,
    voidedByEmail: null,
    voidReason: null,
    latestRunStatus: 'not_run' as const,
  })),
  ...ORPHAN_REQUESTS_RAW.map(r => ({
    id: `${ORPHAN_SET_ID}-r${r.no}`,
    requestSetId: ORPHAN_SET_ID,
    requestNo: r.no,
    requestText: r.text,
    sourcePageStart: 2 + r.no,
    sourcePageEnd: 2 + r.no,
    isVoided: false,
    voidedAt: null,
    voidedByEmail: null,
    voidReason: null,
    latestRunStatus: 'not_run' as const,
  })),
];

export const MOCK_DOCUMENTS: DocumentMeta[] = [
  {
    id: 'd1', bates: 'DEMO-0000001', docType: 'Email',
    subject: 'RE: Licensing Agreement — revised draft v3',
    aiSummary: 'Whitaker transmits revised licensing agreement draft to Beacon counsel; Delacroix CC\'d on §4.2 royalty terms.',
    shortSummary: 'Whitaker transmits revised licensing agreement draft to Beacon counsel; Delacroix CC\'d on §4.2 royalty terms.',
    from: 'm.whitaker@acme-demo.example',
    to: ['counsel@beacon-demo.example'],
    cc: ['e.delacroix@acme-demo.example'],
    date: '2022-02-15',
    productionSource: 'Acme Production 001',
    productionId: 'prod-acme-001',
    familyPosition: 'parent', hasChildren: true, parentBates: 'DEMO-0000001', parentId: null, childIds: ['d1a'],
    issueIds: ['iss-breach'],
    docViewerUrl: null,
  },
  {
    id: 'd1a', bates: 'DEMO-0000002', docType: 'Contract',
    subject: 'Licensing-Agreement-Draft-v3.docx',
    aiSummary: 'Draft licensing agreement v3 with marked-up §4.2 minimum royalty clause.',
    shortSummary: 'Draft licensing agreement v3 with marked-up §4.2 minimum royalty clause.',
    from: null, to: [], cc: [], date: '2022-02-15',
    productionSource: 'Acme Production 001',
    productionId: 'prod-acme-001',
    familyPosition: 'child', hasChildren: false, parentBates: 'DEMO-0000001', parentId: 'd1', childIds: [],
    issueIds: ['iss-breach', 'iss-royalties'],
    docViewerUrl: null,
  },
  {
    id: 'd2', bates: 'DEMO-0000010', docType: 'Email',
    subject: 'Q2 2023 royalty schedule',
    aiSummary: 'Bookkeeper transmits Q2 2023 royalty distribution schedule; flags a shortfall month.',
    shortSummary: 'Bookkeeper transmits Q2 2023 royalty distribution schedule; flags a shortfall month.',
    from: 'bookkeeping@acme-demo.example',
    to: ['m.whitaker@acme-demo.example'],
    cc: [],
    date: '2023-07-10',
    productionSource: 'Acme Production 001',
    productionId: 'prod-acme-001',
    familyPosition: 'parent', hasChildren: true, parentBates: 'DEMO-0000010', parentId: null, childIds: ['d2a'],
    issueIds: ['iss-royalties'],
    docViewerUrl: null,
  },
  {
    id: 'd2a', bates: 'DEMO-0000011', docType: 'Spreadsheet',
    subject: 'Q2-2023-royalty-schedule.xlsx',
    aiSummary: 'Monthly royalty distribution schedule showing an $18k shortfall in June 2023.',
    shortSummary: 'Monthly royalty distribution schedule showing an $18k shortfall in June 2023.',
    from: null, to: [], cc: [], date: '2023-07-10',
    productionSource: 'Acme Production 001',
    productionId: 'prod-acme-001',
    familyPosition: 'child', hasChildren: false, parentBates: 'DEMO-0000010', parentId: 'd2', childIds: [],
    issueIds: ['iss-royalties', 'iss-damages'],
    docViewerUrl: null,
  },
  {
    id: 'd3', bates: 'DEMO-0000024', docType: 'Email',
    subject: 'Notice of default — May 2023',
    aiSummary: 'Beacon counsel transmits notice of default to Whitaker; copies Delacroix.',
    shortSummary: 'Beacon counsel transmits notice of default to Whitaker; copies Delacroix.',
    from: 'counsel@beacon-demo.example',
    to: ['m.whitaker@acme-demo.example'],
    cc: ['e.delacroix@acme-demo.example'],
    date: '2023-05-03',
    productionSource: 'Beacon Production 001',
    productionId: 'prod-beacon-001',
    familyPosition: 'loose', hasChildren: false, parentBates: null, parentId: null, childIds: [],
    issueIds: ['iss-breach'],
    docViewerUrl: null,
  },
  {
    id: 'd4', bates: 'DEMO-0000038', docType: 'Email',
    subject: 'Internal note — damages workup',
    aiSummary: 'CFO forwards draft damages workup prepared at direction of counsel in anticipation of litigation.',
    shortSummary: 'CFO forwards draft damages workup prepared at direction of counsel in anticipation of litigation.',
    from: 'cfo@acme-demo.example',
    to: ['m.whitaker@acme-demo.example'],
    cc: ['counsel@acme-demo.example'],
    date: '2023-09-12',
    productionSource: 'Acme Production 002',
    productionId: 'prod-acme-002',
    familyPosition: 'loose', hasChildren: false, parentBates: null, parentId: null, childIds: [],
    issueIds: ['iss-damages', 'iss-privilege'],
    docViewerUrl: null,
  },
  {
    id: 'd5', bates: 'DEMO-0000052', docType: 'Email',
    subject: 'RE: licensing — Delacroix / Whitaker sync',
    aiSummary: 'Delacroix and Whitaker align on licensing strategy ahead of negotiation.',
    shortSummary: 'Delacroix and Whitaker align on licensing strategy ahead of negotiation.',
    from: 'e.delacroix@acme-demo.example',
    to: ['m.whitaker@acme-demo.example'],
    cc: [],
    date: '2022-02-10',
    productionSource: 'Acme Production 001',
    productionId: 'prod-acme-001',
    familyPosition: 'loose', hasChildren: false, parentBates: null, parentId: null, childIds: [],
    issueIds: ['iss-breach'],
    docViewerUrl: null,
  },
  // ── Outgoing (our-side) collected docs ──────────────────────────────
  // Whitaker collection: 2 produced + 1 not_produced → produced count 2.
  {
    id: 'o1', bates: 'WHIT-000001', docType: 'Email',
    subject: 'Licensing royalties — internal summary',
    aiSummary: 'Whitaker summarizes royalty position for internal counsel ahead of production.',
    shortSummary: 'Whitaker summarizes royalty position for internal counsel ahead of production.',
    from: 'whitaker@example.com', to: ['counsel@example.com'], cc: [],
    date: '2023-06-01',
    productionSource: 'Whitaker Custodial Collection', productionId: 'prod-out-whitaker',
    familyPosition: 'loose', hasChildren: false, parentBates: null, parentId: null, childIds: [],
    issueIds: ['iss-royalties'], docViewerUrl: null,
    productionStatus: 'produced',
  },
  {
    id: 'o2', bates: 'WHIT-000002', docType: 'Spreadsheet',
    subject: 'Whitaker-royalty-ledger.xlsx',
    aiSummary: 'Ledger of royalty receipts 2021–2023 produced to opposing counsel.',
    shortSummary: 'Ledger of royalty receipts 2021–2023 produced to opposing counsel.',
    from: null, to: [], cc: [], date: '2023-06-02',
    productionSource: 'Whitaker Custodial Collection', productionId: 'prod-out-whitaker',
    familyPosition: 'loose', hasChildren: false, parentBates: null, parentId: null, childIds: [],
    issueIds: ['iss-royalties', 'iss-damages'], docViewerUrl: null,
    productionStatus: 'produced',
  },
  {
    id: 'o3', bates: 'WHIT-000003', docType: 'Email',
    subject: 'DRAFT — do not produce (attorney work product)',
    aiSummary: 'Draft strategy memo withheld from production; still under review.',
    shortSummary: 'Draft strategy memo withheld from production; still under review.',
    from: 'counsel@example.com', to: ['whitaker@example.com'], cc: [],
    date: '2023-06-03',
    productionSource: 'Whitaker Custodial Collection', productionId: 'prod-out-whitaker',
    familyPosition: 'loose', hasChildren: false, parentBates: null, parentId: null, childIds: [],
    issueIds: [], docViewerUrl: null,
    productionStatus: 'not_produced',
  },
  // Delacroix collection: 1 produced + 1 withheld_privileged → produced count 1.
  {
    id: 'o4', bates: 'DELA-000001', docType: 'Email',
    subject: 'Negotiation timeline (produced)',
    aiSummary: 'Delacroix reconstructs licensing negotiation timeline; produced to opposing counsel.',
    shortSummary: 'Delacroix reconstructs licensing negotiation timeline; produced to opposing counsel.',
    from: 'delacroix@example.com', to: ['opposing@example.com'], cc: [],
    date: '2022-01-20',
    productionSource: 'Delacroix Custodial Collection', productionId: 'prod-out-delacroix',
    familyPosition: 'loose', hasChildren: false, parentBates: null, parentId: null, childIds: [],
    issueIds: ['iss-breach'], docViewerUrl: null,
    productionStatus: 'produced',
  },
  {
    id: 'o5', bates: 'DELA-000002', docType: 'Email',
    subject: 'Privileged — counsel advice on breach exposure',
    aiSummary: 'Delacroix relays counsel advice on breach exposure; withheld on privilege.',
    shortSummary: 'Delacroix relays counsel advice on breach exposure; withheld on privilege.',
    from: 'counsel@example.com', to: ['delacroix@example.com'], cc: [],
    date: '2022-01-21',
    productionSource: 'Delacroix Custodial Collection', productionId: 'prod-out-delacroix',
    familyPosition: 'loose', hasChildren: false, parentBates: null, parentId: null, childIds: [],
    issueIds: ['iss-privilege'], docViewerUrl: null,
    productionStatus: 'withheld_privileged',
  },
  // Unproduced collection: nothing produced → collection is dropped from grid.
  {
    id: 'o6', bates: 'CFO-000001', docType: 'Email',
    subject: 'CFO working notes (not produced)',
    aiSummary: 'CFO internal working notes; not yet produced.',
    shortSummary: 'CFO internal working notes; not yet produced.',
    from: 'cfo@example.com', to: ['counsel@example.com'], cc: [],
    date: '2023-09-20',
    productionSource: 'CFO Working Set (unproduced)', productionId: 'prod-out-unproduced',
    familyPosition: 'loose', hasChildren: false, parentBates: null, parentId: null, childIds: [],
    issueIds: [], docViewerUrl: null,
    productionStatus: 'not_produced',
  },

  // ── Our-side (outgoing) collection documents — the client's own custodial
  // files that an RFP match run scans. Attributed to prod-client-collection-001
  // (Whitaker + Delacroix mailboxes) and prod-client-collection-002 (Whitaker
  // personal files). Without these rows the Documents tab / View-all shows
  // "No documents match" for an our-side collection even though the collection
  // card carries a doc count. (The mock's /documents/search filters by
  // production_id / direction, not per-doc custodian, so custodian sub-cards
  // don't further narrow locally — a known mock limitation.)
  {
    id: 'oc1', bates: 'OUR-0000001', docType: 'Email',
    subject: 'Internal — licensing negotiation strategy',
    aiSummary: 'Whitaker circulates the internal negotiation strategy memo for the licensing agreement to the deal team.',
    shortSummary: 'Whitaker circulates internal licensing negotiation strategy to the deal team.',
    from: 'm.whitaker@example.com',
    to: ['deal-team@example.com'],
    cc: ['e.delacroix@example.com'],
    date: '2022-01-28',
    productionSource: 'Client Collection — Custodial Mailboxes',
    productionId: 'prod-client-collection-001',
    familyPosition: 'parent', hasChildren: true, parentBates: 'OUR-0000001', parentId: null, childIds: ['oc1a'],
    issueIds: ['iss-negotiation', 'iss-breach'],
    docViewerUrl: null,
  },
  {
    id: 'oc1a', bates: 'OUR-0000002', docType: 'Memo',
    subject: 'Licensing-Negotiation-Strategy.docx',
    aiSummary: 'Internal strategy memo outlining target royalty floor and fallback positions ahead of the licensing negotiation.',
    shortSummary: 'Internal strategy memo: target royalty floor + fallback positions.',
    from: null, to: [], cc: [], date: '2022-01-28',
    productionSource: 'Client Collection — Custodial Mailboxes',
    productionId: 'prod-client-collection-001',
    familyPosition: 'child', hasChildren: false, parentBates: 'OUR-0000001', parentId: 'oc1', childIds: [],
    issueIds: ['iss-negotiation'],
    docViewerUrl: null,
  },
  {
    id: 'oc2', bates: 'OUR-0000010', docType: 'Email',
    subject: 'Royalty payment records — 2022–2023',
    aiSummary: 'Delacroix forwards the compiled royalty payment records for 2022–2023 to counsel in response to the document request.',
    shortSummary: 'Delacroix forwards compiled 2022–2023 royalty payment records to counsel.',
    from: 'e.delacroix@example.com',
    to: ['counsel@example.com'],
    cc: [],
    date: '2023-08-14',
    productionSource: 'Client Collection — Custodial Mailboxes',
    productionId: 'prod-client-collection-001',
    familyPosition: 'loose', hasChildren: false, parentBates: null, parentId: null, childIds: [],
    issueIds: ['iss-royalties', 'iss-financials'],
    docViewerUrl: null,
  },
  {
    id: 'oc3', bates: 'OUR-0000020', docType: 'Email',
    subject: 'RE: May 2023 notice of default — internal response',
    aiSummary: 'Whitaker and Delacroix coordinate the internal response to the May 2023 notice of default.',
    shortSummary: 'Whitaker + Delacroix coordinate internal response to the May 2023 default notice.',
    from: 'm.whitaker@example.com',
    to: ['e.delacroix@example.com'],
    cc: [],
    date: '2023-05-08',
    productionSource: 'Client Collection — Custodial Mailboxes',
    productionId: 'prod-client-collection-001',
    familyPosition: 'loose', hasChildren: false, parentBates: null, parentId: null, childIds: [],
    issueIds: ['iss-default', 'iss-breach'],
    docViewerUrl: null,
  },
  {
    id: 'oc4', bates: 'OUR-0000030', docType: 'Spreadsheet',
    subject: 'Damages-workup-model.xlsx',
    aiSummary: 'Internal damages model estimating lost royalty income attributable to the alleged breach.',
    shortSummary: 'Internal damages model: lost royalty income from the alleged breach.',
    from: null, to: [], cc: [], date: '2023-09-02',
    productionSource: 'Client Collection — Custodial Mailboxes',
    productionId: 'prod-client-collection-001',
    familyPosition: 'loose', hasChildren: false, parentBates: null, parentId: null, childIds: [],
    issueIds: ['iss-damages'],
    docViewerUrl: null,
  },
  {
    id: 'oc5', bates: 'OUR-0000100', docType: 'Email',
    subject: 'Whitaker personal — licensing agreement copy',
    aiSummary: 'Whitaker retains a personal copy of the executed licensing agreement in his own files.',
    shortSummary: 'Whitaker retains a personal copy of the executed licensing agreement.',
    from: 'm.whitaker@example.com',
    to: ['m.whitaker.personal@example.com'],
    cc: [],
    date: '2021-11-30',
    productionSource: 'Whitaker Personal Files',
    productionId: 'prod-client-collection-002',
    familyPosition: 'loose', hasChildren: false, parentBates: null, parentId: null, childIds: [],
    issueIds: ['iss-breach'],
    docViewerUrl: null,
  },
  {
    id: 'oc6', bates: 'OUR-0000101', docType: 'Note',
    subject: 'Handwritten notes — royalty dispute timeline',
    aiSummary: 'Whitaker’s handwritten notes reconstructing the royalty dispute timeline.',
    shortSummary: 'Whitaker’s handwritten notes: royalty dispute timeline.',
    from: null, to: [], cc: [], date: '2023-06-15',
    productionSource: 'Whitaker Personal Files',
    productionId: 'prod-client-collection-002',
    familyPosition: 'loose', hasChildren: false, parentBates: null, parentId: null, childIds: [],
    issueIds: ['iss-royalties'],
    docViewerUrl: null,
  },
  {
    id: 'oc7', bates: 'OUR-0000102', docType: 'Email',
    subject: 'FWD: default notice — personal copy',
    aiSummary: 'Whitaker forwards a personal copy of the May 2023 default notice to his own files.',
    shortSummary: 'Whitaker forwards a personal copy of the default notice to his own files.',
    from: 'm.whitaker@example.com',
    to: ['m.whitaker.personal@example.com'],
    cc: [],
    date: '2023-05-04',
    productionSource: 'Whitaker Personal Files',
    productionId: 'prod-client-collection-002',
    familyPosition: 'loose', hasChildren: false, parentBates: null, parentId: null, childIds: [],
    issueIds: ['iss-default'],
    docViewerUrl: null,
  },
];

// `matchedElement` is the short phrase (<=100 chars) copied or paraphrased
// from the doc's aiSummary that establishes responsiveness. The UI looks
// for this span inside the summary and paints a gentle highlight over it.
// Phrases below are exact substrings of the corresponding MOCK_DOCUMENTS
// `aiSummary` so the highlighter finds them without fuzzy matching.
const MATCH_SEEDS: Array<{
  docId: string;
  reqNos: number[];
  confidence: number;
  reasoning: string;
  matchedElement: string;
}> = [
  {
    docId: 'd1', reqNos: [1, 2, 7], confidence: 0.94,
    reasoning: 'Direct Whitaker/Beacon communication transmitting a licensing-agreement draft. Hits COMMUNICATIONS and LICENSING AGREEMENT categories.',
    matchedElement: 'revised licensing agreement draft to Beacon counsel',
  },
  {
    docId: 'd2', reqNos: [3, 5], confidence: 0.89,
    reasoning: 'Royalty distribution schedule — squarely within royalty and damages requests.',
    matchedElement: 'Q2 2023 royalty distribution schedule',
  },
  {
    docId: 'd3', reqNos: [1, 4, 8], confidence: 0.93,
    reasoning: 'Notice of default directly targets breach and the May 2023 notice request.',
    matchedElement: 'notice of default to Whitaker',
  },
  {
    docId: 'd4', reqNos: [5], confidence: 0.72,
    reasoning: 'Internal damages workup. Possibly privileged — reviewer should confirm.',
    matchedElement: 'draft damages workup',
  },
  {
    docId: 'd5', reqNos: [6], confidence: 0.81,
    reasoning: 'Delacroix/Whitaker pre-negotiation alignment on the licensing agreement.',
    matchedElement: 'align on licensing strategy ahead of negotiation',
  },
];

const DELACROIX_MATCH_SEEDS: Array<{
  docId: string;
  reqNos: number[];
  confidence: number;
  reasoning: string;
  matchedElement: string;
}> = [
  {
    docId: 'd1', reqNos: [1, 2, 3], confidence: 0.88,
    reasoning: 'Licensing agreement draft with Delacroix CC\'d — responsive to her agreement and Whitaker-communications requests.',
    matchedElement: 'Delacroix CC\'d on §4.2 royalty terms',
  },
  {
    docId: 'd3', reqNos: [1], confidence: 0.85,
    reasoning: 'Notice of default routed to Delacroix — direct PROPOUNDING PARTY communication.',
    matchedElement: 'copies Delacroix',
  },
  {
    docId: 'd5', reqNos: [3], confidence: 0.90,
    reasoning: 'Delacroix/Whitaker sync about licensing — exact match for the Delacroix-Whitaker communications request.',
    matchedElement: 'Delacroix and Whitaker align on licensing strategy',
  },
];

const SEEDED_CONFIRMED: Record<string, boolean> = {
  d1: true, d2: true, d3: true,
};

export const MOCK_MATCHES: RequestMatch[] = [
  ...MATCH_SEEDS.flatMap(seed =>
    seed.reqNos.map(no => ({
      id: `match-${seed.docId}-r${no}`,
      requestItemId: `${WHITAKER_SET_ID}-r${no}`,
      documentId: seed.docId,
      requestNo: no,
      requestSetId: WHITAKER_SET_ID,
      requestSetLabel: 'Whitaker RFP',
      llmReasoning: seed.reasoning,
      llmConfidence: seed.confidence,
      llmIsMatch: true,
      matchOrigin: 'ai' as const,
      llmMatchedElement: seed.matchedElement,
      reviewerConfirmed: SEEDED_CONFIRMED[seed.docId] ?? null,
      perRequestNote: '',
      aiSuggestedObjectionTypes: [],
    })),
  ),
  ...DELACROIX_MATCH_SEEDS.flatMap(seed =>
    seed.reqNos.map(no => ({
      id: `match-delacroix-${seed.docId}-r${no}`,
      requestItemId: `${DELACROIX_SET_ID}-r${no}`,
      documentId: seed.docId,
      requestNo: no,
      requestSetId: DELACROIX_SET_ID,
      requestSetLabel: 'Delacroix RFP',
      llmReasoning: seed.reasoning,
      llmConfidence: seed.confidence,
      llmIsMatch: true,
      matchOrigin: 'ai' as const,
      llmMatchedElement: seed.matchedElement,
      reviewerConfirmed: SEEDED_CONFIRMED[seed.docId] ?? null,
      perRequestNote: '',
      aiSuggestedObjectionTypes: [],
    })),
  ),
];

function emptyReview(docId: string): DocumentReview {
  return {
    documentId: docId,
    privilege: { type: null, basis: '', description: '' },
    flags: { aeo: false, needs_redaction: false, client_notify: false },
    classification: null,
    status: 'unreviewed',
    escalation: { to: '', reason: null, notes: '' },
    note: '',
    reviewedBy: null,
    reviewedAt: null,
    viewedAt: null,
  };
}

const REVIEWED_BY = 'demo@geniac.io';
const REVIEWED_AT = '2026-04-20T10:00:00Z';

function tag(docId: string, patch: Partial<DocumentReview>): DocumentReview {
  const base = emptyReview(docId);
  return {
    ...base,
    ...patch,
    privilege: { ...base.privilege, ...(patch.privilege ?? {}) },
    flags: { ...base.flags, ...(patch.flags ?? {}) },
    escalation: { ...base.escalation, ...(patch.escalation ?? {}) },
    reviewedBy: REVIEWED_BY,
    reviewedAt: REVIEWED_AT,
  };
}

const SEEDED_REVIEWS: Record<string, DocumentReview> = {
  d1: tag('d1', { status: 'coded', classification: 'hot' }),
  // A reviewed attachment with state unlike its parent makes the child-row
  // privilege, flag, and disposition affordances visible in the mock app.
  d1a: tag('d1a', {
    status: 'flagged_for_partner',
    privilege: {
      type: 'attorney_client',
      basis: 'Marked up for counsel review.',
      description: 'Counsel comments on draft license terms.',
    },
    flags: { aeo: true, needs_redaction: false, client_notify: false },
  }),
  d2: tag('d2', { status: 'coded' }),
  d3: tag('d3', { status: 'coded' }),
  d4: tag('d4', {
    status: 'flagged_for_partner',
    privilege: {
      type: 'work_product',
      basis: 'Prepared at direction of counsel in anticipation of litigation.',
      description: 'Internal damages workup.',
    },
    escalation: { to: 'demo@geniac.io', reason: 'privilege_question', notes: 'Confirm work-product protection.' },
  }),
};

export const MOCK_REVIEWS: DocumentReview[] = MOCK_DOCUMENTS.map(
  d => SEEDED_REVIEWS[d.id] ?? emptyReview(d.id),
);

export const MOCK_OBJECTIONS: RequestObjection[] = [
  {
    requestItemId: `${WHITAKER_SET_ID}-r1`,
    objectionTypes: ['overbroad', 'unduly_burdensome'],
    objectionText:
      'Objection. The Request is overbroad as to time and scope, and unduly burdensome.',
    responseStatus: 'subject_to_objections',
    scopeLimitation:
      'Subject to and without waiving the foregoing objections, Responding Party will produce non-privileged communications with Propounding Party from January 1, 2022 through the present relating to the subject matter of this action.',
    draftedBy: 'demo@geniac.io',
    draftedAt: '2026-03-18T14:00:00Z',
    servedAt: null,
    satisfied: false,
  },
  {
    requestItemId: `${WHITAKER_SET_ID}-r5`,
    objectionTypes: ['premature_expert'],
    objectionText:
      'Objection. Damages are the subject of expert testimony and discovery is ongoing.',
    responseStatus: 'objection_only',
    scopeLimitation: '',
    draftedBy: 'demo@geniac.io',
    draftedAt: '2026-03-20T09:15:00Z',
    servedAt: null,
    satisfied: false,
  },
];

// Doc-issue assignments: one row per (document, issue) tag. Derived from the
// issueIds already embedded on each MOCK_DOCUMENT so the dashboard's per-issue
// "X docs" counts are internally consistent with what the review flows show.
export const MOCK_DOC_ISSUE_ASSIGNMENTS: { documentId: string; issueId: string }[] =
  MOCK_DOCUMENTS.flatMap(d => d.issueIds.map(issueId => ({ documentId: d.id, issueId })));
